import { useEffect, useMemo, useRef } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import { useMap } from '../../contexts/MapContext';
import { useAuth } from '../../contexts/AuthContext';
import { passivePerception, PASSIVE_PERCEPTION_RANGE, tokenDistance } from '../../lib/perception';

// Always-on passive perception. Whenever the player's token, position, passive
// score, or the world's hidden things change, scan everything in passive range
// and auto-discover anything whose DC ≤ passive perception.
//
// Renders nothing. Mount once in PlayerView. Writes are idempotent — a hit on
// `discoveredBy[playerId]` already set is a no-op.

export default function PassivePerceptionWatcher() {
  const { traps, tokens, players, updateTrap, updateToken, teleports, updateTeleport } = useCampaign();
  const { doors, updateDoor } = useMap();
  const { auth } = useAuth();
  const playerId = auth?.playerId;

  // My token position (drives sight range). Stable identity — only changes
  // when the player's *own* token's x/y change, not on every snapshot of
  // some other token (which would otherwise re-run the discovery scan every
  // time the DM nudges a monster).
  const myTokenRaw = useMemo(() => {
    if (!playerId) return null;
    return Object.values(tokens || {}).find((t) => t.ownerId === playerId) || null;
  }, [tokens, playerId]);
  const myX = myTokenRaw?.x ?? null;
  const myY = myTokenRaw?.y ?? null;
  const myTokenId = myTokenRaw?.id ?? null;

  const passive = useMemo(
    () => passivePerception(playerId ? players?.[playerId] : null),
    [players, playerId]
  );

  // Track what we've already written so we don't spam Firestore if a snapshot
  // round-trips before the discovery field comes back.
  const writtenRef = useRef({ trapIds: new Set(), tokenIds: new Set(), doorIds: new Set(), teleportIds: new Set() });

  useEffect(() => {
    if (!playerId || myX == null || myY == null || passive == null) return;
    const myToken = { x: myX, y: myY, id: myTokenId };

    const stamp = Date.now();

    // ── Traps ─────────────────────────────────────────────────────────────
    for (const t of Object.values(traps || {})) {
      if (!t || t.triggered) continue;
      if (t.discoveryDC == null) continue;
      if (t.discoveredBy?.[playerId]) continue;
      if (writtenRef.current.trapIds.has(t.id)) continue;
      if (tokenDistance(myToken, t) > PASSIVE_PERCEPTION_RANGE) continue;
      if (passive < t.discoveryDC) continue;
      writtenRef.current.trapIds.add(t.id);
      updateTrap(t.id, { [`discoveredBy.${playerId}`]: stamp });
    }

    // ── Stealthed tokens ─────────────────────────────────────────────────
    for (const tk of Object.values(tokens || {})) {
      if (!tk || tk.id === myToken.id) continue;
      const isStealthed = (tk.buffs || []).some((b) => b.id === 'stealthed');
      if (!isStealthed || tk.stealthDC == null) continue;
      if (tk.discoveredBy?.[playerId]) continue;
      if (writtenRef.current.tokenIds.has(tk.id)) continue;
      if (tokenDistance(myToken, tk) > PASSIVE_PERCEPTION_RANGE) continue;
      if (passive < tk.stealthDC) continue;
      writtenRef.current.tokenIds.add(tk.id);
      updateToken(tk.id, { [`discoveredBy.${playerId}`]: stamp });
    }

    // ── Secret doors (GLOBAL reveal, same as perception roll) ─────────────
    for (const d of doors || []) {
      if (!d || d.type !== 'secret') continue;
      if (d.secretRevealed) continue;
      if (d.discoveryDC == null) continue;
      if (writtenRef.current.doorIds.has(d.id)) continue;
      const a = d.pts?.[0];
      const b = d.pts?.[d.pts.length - 1];
      if (!a || !b) continue;
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      if (tokenDistance(myToken, mid) > PASSIVE_PERCEPTION_RANGE) continue;
      if (passive < d.discoveryDC) continue;
      writtenRef.current.doorIds.add(d.id);
      updateDoor(d.id, {
        secretRevealed: true,
        style: 'wood',
        state: 'locked',
      });
    }

    // ── Hidden teleports (trapdoors) — per-player reveal ───────────────────
    for (const tp of Object.values(teleports || {})) {
      if (!tp.hidden) continue;
      if (tp.discoveryDC == null) continue;
      if (tp.discoveredBy?.[playerId]) continue;
      if (writtenRef.current.teleportIds.has(tp.id)) continue;
      const dA = tokenDistance(myToken, tp.a);
      const dB = tokenDistance(myToken, tp.b);
      if (Math.min(dA, dB) > PASSIVE_PERCEPTION_RANGE) continue;
      if (passive < tp.discoveryDC) continue;
      writtenRef.current.teleportIds.add(tp.id);
      updateTeleport(tp.id, { [`discoveredBy.${playerId}`]: stamp });
    }
  }, [playerId, myX, myY, myTokenId, passive, traps, tokens, doors, teleports, updateTrap, updateToken, updateDoor, updateTeleport]);

  return null;
}
